/* eslint-disable @typescript-eslint/camelcase */
import cloneDeep from 'lodash/cloneDeep'
import { DispatchProp } from 'react-redux'
import moment from 'moment'

import { TRIDENT_ROUTE_COMPOSER_API } from '~/constants'
import {
  Circulation,
  PubstackDocument,
  PubstackRevision,
  Story,
  ComposerApiErrorResponse,
  Revision,
  Lock,
  ScheduledEvents,
} from '~/types'
import { setLastActiveTime } from '~/reactapp/App/LockManager/activity-utils'
import { getFeatures } from '~/featureFlags'
import { AppError } from './AppError'
import { setStoryOperationInProgress } from '~/redux/appReducer'
import { FetchError } from 'react-async'

let arcPubETag: string | null | undefined

// Angular story service will use the "legacy story" until it is refactored to use the Pubstack data model
export function makeLegacyStory(
  document: PubstackDocument,
  revision: PubstackRevision
): Story {
  return {
    ...revision.ans,
    _id: revision.document_id,
    publish_date: document.last_published_at,
    first_publish_date: document.first_published_at,
    created_date: document.created_at,
    revision: {
      revision_id: revision.id,
      user_id: revision.user_id,
    },
    additional_properties: {
      ...revision.ans.additional_properties,
      is_published: Boolean(document.published_revision_id),
      publish_date: document.last_published_at,
    },
  }
}

function removeLegacyStoryData(ans: Story) {
  const ansClone = cloneDeep(ans)
  delete ansClone.publish_date
  delete ansClone.first_publish_date
  delete ansClone.created_date
  delete ansClone.revision
  delete ansClone.additional_properties?.is_published
  delete ansClone.additional_properties?.publish_date
  delete ansClone.isDirty
  return ansClone
}

interface LegacyRevisionHistoryItem extends Story {
  revision: {
    revision_id: string
    user_id?: string
    index: number
    branch: 'default'
    parent_id?: string
  }
  pubHistory: [
    {
      publish_date?: string
      unpublish_date?: string
      user_id?: string
    }?
  ]
}

function makeLegacyRevisionList(
  document: PubstackDocument,
  revisions: PubstackRevision[]
) {
  const unpublishedDates = new Map<string, string>()
  revisions
    .filter((rev) => rev.type === 'PUBLISHED')
    .reverse() // Pubstack reversed order of rev history
    .forEach((publishedRevision, index, allPublishedRevisions) => {
      if (index + 1 < allPublishedRevisions.length) {
        // This revision is considered unpublished when the next was published
        unpublishedDates.set(
          publishedRevision.id,
          allPublishedRevisions[index + 1].created_at
        )
      } else if (
        // Latest published revision is not currently published
        document.published_revision_id !== publishedRevision.id
      ) {
        unpublishedDates.set(
          publishedRevision.id,
          document.last_unpublished_at as string
        )
      }

      // Else - latest published revision is currently published
    })

  // Configure parent id and apply index value
  const legacyFormatData = revisions.map((rev, index, array) => {
    const legacyStory = makeLegacyStory(document, rev)

    const legacyRevisionHistoryItem: LegacyRevisionHistoryItem = {
      ...legacyStory,
      pubHistory: [],
      revision: {
        ...(legacyStory.revision as Revision),
        index: array.length - index,
        branch: 'default',
      },
    }
    // First revision will not have parent ID
    if (index !== 0) {
      legacyRevisionHistoryItem.revision.parent_id = revisions[index - 1].id
    }

    if (rev.type === 'PUBLISHED') {
      legacyRevisionHistoryItem.pubHistory.push({
        publish_date: rev.ans.publish_date,
        user_id: rev.ans.revision?.user_id,
        unpublish_date: unpublishedDates.get(
          legacyRevisionHistoryItem.revision.revision_id
        ),
      })
    }

    return legacyRevisionHistoryItem
  })
  return legacyFormatData
}

async function handleError(errorOrResponse: Error | AppError | Response) {
  console.error('errorOrResponse', errorOrResponse)
  if (errorOrResponse instanceof AppError) {
    return errorOrResponse
  } else if (errorOrResponse instanceof Response) {
    const json: ComposerApiErrorResponse = await errorOrResponse.json()
    return new AppError({
      errorData: json.error.errorData,
      errorKey: json.error.errorKey,
      status: errorOrResponse.status,
      statusText: errorOrResponse.statusText,
    })
  } else {
    return new AppError({ errorData: errorOrResponse.message })
  }
}

export async function updateDraftRevision(
  documentId: string,
  ans: Story,
  dispatch: DispatchProp['dispatch']
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )

  if (ans?.planning?.scheduling?.planned_publish_date) {
    const formattedTz = new Date(ans.planning.scheduling.planned_publish_date)
    const newDate = moment.utc(formattedTz)
    ans.planning.scheduling.planned_publish_date = newDate
  }

  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
    setLastActiveTime(documentId)
  }

  try {
    dispatch(setStoryOperationInProgress(true))

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'If-Match': arcPubETag ?? '',
      },
      body: JSON.stringify(removeLegacyStoryData(ans)),
    })

    if (!response.ok) {
      throw response
    }

    arcPubETag = response.headers.get('Arcpub-ETag')

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    dispatch(setStoryOperationInProgress(false))

    return {
      document: json.document,
      revision: json.revision,
      legacyStory: makeLegacyStory(json.document, json.revision),
    }
  } catch (error) {
    dispatch(setStoryOperationInProgress(false))
    throw await handleError(error)
  }
}

export async function createStoryPubstack(
  story: Story,
  dispatch: DispatchProp['dispatch']
) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    dispatch(setStoryOperationInProgress(true))

    delete story.isDirty

    if (story?.planning?.scheduling?.planned_publish_date) {
      const formattedTz = new Date(
        story.planning.scheduling.planned_publish_date
      )
      const newDate = moment.utc(formattedTz)
      story.planning.scheduling.planned_publish_date = newDate
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(story),
    })

    if (!response.ok) {
      throw response
    }

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    dispatch(setStoryOperationInProgress(false))

    return {
      document: json.document,
      revision: json.revision,
      legacyStory: makeLegacyStory(json.document, json.revision),
    }
  } catch (error) {
    dispatch(setStoryOperationInProgress(false))
    throw await handleError(error)
  }
}

export async function getStoryPubstack(documentId: string) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())

    if (!response.ok) {
      throw response
    }

    arcPubETag = response.headers.get('Arcpub-ETag')

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    return {
      status: response.status,
      data: {
        document: json.document,
        revision: json.revision,
        legacyStory: makeLegacyStory(json.document, json.revision),
      },
    }
  } catch (errorOrResponse) {
    const formattedError = {
      status: errorOrResponse.status,
      statusText: errorOrResponse.statusText,
      data:
        errorOrResponse instanceof Response
          ? await errorOrResponse.json()
          : errorOrResponse.message,
    }
    throw formattedError
  }
}

interface BodyWithCleanedStory {
  story: Story
  displayDate?: string | undefined
  publishDate?: string | undefined
}

export async function publishStoryPubstack(
  bodyWithCleanedStory: BodyWithCleanedStory,
  dispatch: DispatchProp['dispatch']
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const documentId = bodyWithCleanedStory.story._id
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v3/story/${documentId}/revision/publish`,
    window.location.origin
  )

  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
    setLastActiveTime(documentId)
  }

  delete bodyWithCleanedStory.story.isDirty

  try {
    dispatch(setStoryOperationInProgress(true))
    bodyWithCleanedStory.story.additional_properties = {
      ...bodyWithCleanedStory.story.additional_properties,
      is_published: true,
    }
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'If-Match': arcPubETag ?? '',
      },
      body: JSON.stringify(bodyWithCleanedStory),
    })

    if (!response.ok) {
      throw response
    }

    arcPubETag = response.headers.get('Arcpub-ETag')

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    return {
      document: json.document,
      revision: json.revision,
      legacyStory: makeLegacyStory(json.document, json.revision),
    }
  } catch (error) {
    throw await handleError(error)
  } finally {
    dispatch(setStoryOperationInProgress(false))
  }
}

export async function unpublishStoryPubstack(
  documentId: string,
  dispatch: DispatchProp['dispatch']
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/revision/publish`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
  }

  try {
    dispatch(setStoryOperationInProgress(true))

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw response
    }

    arcPubETag = response.headers.get('Arcpub-ETag')

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    return {
      document: json.document,
      revision: json.revision,
      legacyStory: makeLegacyStory(json.document, json.revision),
    }
  } catch (error) {
    throw await handleError(error)
  } finally {
    dispatch(setStoryOperationInProgress(false))
  }
}

export async function getRevisionDiff(storyId, revisionId) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${storyId}/revision/${revisionId}/diff`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())

    if (!response.ok) {
      throw response
    }

    const json = await response.json()

    return json
  } catch (errorOrResponse) {
    const formattedError = {
      status: errorOrResponse.status,
      statusText: errorOrResponse.statusText,
      data:
        errorOrResponse instanceof Response
          ? await errorOrResponse.json()
          : errorOrResponse.message,
    }
    throw formattedError
  }
}

export async function getRevision(documentId: string, revisionId: string) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/revision/${revisionId}`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())

    if (!response.ok) {
      throw response
    }

    const json: {
      document: PubstackDocument
      revision: PubstackRevision
    } = await response.json()

    return {
      document: json.document,
      revision: json.revision,
      legacyStory: makeLegacyStory(json.document, json.revision),
    }
  } catch (errorOrResponse) {
    const formattedError = {
      status: errorOrResponse.status,
      statusText: errorOrResponse.statusText,
      data:
        errorOrResponse instanceof Response
          ? await errorOrResponse.json()
          : errorOrResponse.message,
    }
    throw formattedError
  }
}

export async function getRevisionHistory(documentId: string) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/revision`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())

    if (!response.ok) {
      throw response
    }

    const json: {
      document: PubstackDocument
      revisions: PubstackRevision[]
    } = await response.json()

    return {
      revisionList: json.revisions,
      legacyRevisionList: makeLegacyRevisionList(json.document, json.revisions),
    }
  } catch (errorOrResponse) {
    const formattedError = {
      status: errorOrResponse.status,
      statusText: errorOrResponse.statusText,
      data:
        errorOrResponse instanceof Response
          ? await errorOrResponse.json()
          : errorOrResponse.message,
    }
    throw formattedError
  }
}

export async function getCirculation(documentId: string, websiteId: string) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/circulation/${websiteId}`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())
    if (!response.ok) {
      throw response
    }
    const circulation: Circulation = await response.json()
    return circulation
  } catch (errorOrResponse) {
    if (errorOrResponse instanceof Response) {
      const json: ComposerApiErrorResponse = await errorOrResponse.json()
      throw new AppError({
        errorData: json.error.errorData,
        errorKey: json.error.errorKey,
        status: errorOrResponse.status,
        statusText: errorOrResponse.statusText,
      })
    } else {
      throw new AppError({ errorData: errorOrResponse.message })
    }
  }
}

export async function getAllCirculations(documentId: string) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/circulation`,
      window.location.origin
    )
    if ((await getFeatures()).isEnabled('draftApiStateful')) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString())
    if (!response.ok) {
      throw response
    }
    const circulations: Circulation[] = await response.json()
    return circulations
  } catch (errorOrResponse) {
    if (errorOrResponse instanceof Response) {
      const json: ComposerApiErrorResponse = await errorOrResponse.json()
      throw new AppError({
        errorData: json.error.errorData,
        errorKey: json.error.errorKey,
        status: errorOrResponse.status,
        statusText: errorOrResponse.statusText,
      })
    } else {
      throw new AppError({ errorData: errorOrResponse.message })
    }
  }
}

export async function saveCirculation(
  circulation: Circulation,
  queryParams: {
    dryrun?: 'true' | 'false'
    collision_behavior?: 'increment' | 'fail'
  } = {}
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )

  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${circulation.document_id}/circulation/${circulation.website_id}`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
  }
  Object.entries(queryParams).forEach(([param, value]) => {
    if (value) {
      url.searchParams.append(param, value)
    }
  })

  const fetchOption = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(circulation),
  }

  try {
    const response = await fetch(url.toString(), fetchOption)
    if (!response.ok) {
      throw response
    }
    const circulateResult: Circulation = await response.json()
    return circulateResult
  } catch (errorOrResponse) {
    if (errorOrResponse instanceof Response) {
      const json: ComposerApiErrorResponse = await errorOrResponse.json()
      throw new AppError({
        errorData: json.error.errorData,
        errorKey: json.error.errorKey,
        status: errorOrResponse.status,
        statusText: errorOrResponse.statusText,
      })
    } else {
      throw new AppError({ errorData: errorOrResponse.message })
    }
  }
}

export async function bulkSaveCirculations(
  circulations: Circulation[],
  documentId: string,
  isCloning?: boolean
) {
  try {
    const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
      'draftApiStateful'
    )
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/circulation`,
      window.location.origin
    )
    if (isDraftApiStatefulEnabled) {
      url.searchParams.append('stateful', 'true')
    }

    if (isCloning) {
      url.searchParams.append('clone', 'true')
    }

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(circulations),
    })
    if (!response.ok) {
      throw response
    }
    const result: Circulation[] = await response.json()
    return result
  } catch (errorOrResponse) {
    if (errorOrResponse instanceof Response) {
      const json: ComposerApiErrorResponse = await errorOrResponse.json()
      throw new AppError({
        errorData: json.error.errorData,
        errorKey: json.error.errorKey,
        status: errorOrResponse.status,
        statusText: errorOrResponse.statusText,
      })
    } else {
      throw new AppError({ errorData: errorOrResponse.message })
    }
  }
}

export async function concurrentBulkSaveCirculations(
  circulations: Circulation[],
  documentId: string
) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/circulation/regenAll?collision_behavior=increment&dryrun=false`,
      window.location.origin
    )

    /**
     * According to the documentation:
     * https://staging.arcpublishing.com/alc/docs/swagger/?url=./arc-products/sandbox.stateless_draft.json#/Circulation_Operations/draftV1TypeIdCirculationWebsitePut
     * We need to remove or set the website_url to null to execute the url regeneration
     */
    const circulationsWithoutWebsiteUrl = circulations.map((circulation) => {
      return { ...circulation, ...{ website_url: null } }
    })

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(circulationsWithoutWebsiteUrl),
    })
    const results = await response.json()

    if (results.error?.isError) {
      throw new Error(results.error.statusCode)
    }

    return {
      circulations: Object.values(results.circulations),
      error: null,
    }
  } catch (errorOrResponse) {
    return {
      circulations: null,
      error: {
        value: true,
        code: (errorOrResponse as FetchError).message,
      },
    }
  }
}

export async function bulkDeleteCirculations(
  websiteIds: string[],
  documentId: string
) {
  try {
    const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
      'draftApiStateful'
    )
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/circulation`,
      window.location.origin
    )
    if (isDraftApiStatefulEnabled) {
      url.searchParams.append('stateful', 'true')
    }

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ websites: websiteIds }),
    })
    if (!response.ok) {
      throw response
    }
    const result: Circulation[] = await response.json()
    return result
  } catch (errorOrResponse) {
    if (errorOrResponse instanceof Response) {
      const json: ComposerApiErrorResponse = await errorOrResponse.json()
      throw new AppError({
        errorData: json.error.errorData,
        errorKey: json.error.errorKey,
        status: errorOrResponse.status,
        statusText: errorOrResponse.statusText,
      })
    } else {
      throw new AppError({ errorData: errorOrResponse.message })
    }
  }
}

export async function createLock(
  documentId: string,
  attributes: ['DOCUMENT'] | ['CIRCULATIONS'] | ['DOCUMENT', 'CIRCULATIONS']
) {
  try {
    const response = await fetch(`${TRIDENT_ROUTE_COMPOSER_API}/v2/lock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        documentId,
        attributes,
      }),
    })

    if (!response.ok) {
      throw response
    }

    const json: { locks: Lock[] } = await response.json()

    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function deleteLock(
  documentId: string,
  attribute: 'DOCUMENT' | 'CIRCULATION'
) {
  try {
    const response = await fetch(`${TRIDENT_ROUTE_COMPOSER_API}/v2/lock/drop`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        documentId,
        attribute,
      }),
    })

    if (!response.ok) {
      throw response
    }

    const json = await response.json()

    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function updateLock(documentId: string) {
  try {
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/lock`,
      {
        method: 'PUT',
      }
    )

    if (!response.ok) {
      throw response
    }

    const json = await response.json()

    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function getLocks(queryParams: {
  documentIds?: string[]
  attribute?: 'DOCUMENT' | 'CIRCULATIONS'
  after?: string
  dropTracker?: boolean
}) {
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/lock`,
    window.location.origin
  )

  if (queryParams.documentIds) {
    queryParams.documentIds.forEach((docId) => {
      url.searchParams.append('document_id', docId)
    })
  }
  if (queryParams.attribute) {
    url.searchParams.append('attribute', queryParams.attribute)
  }
  if (queryParams.after) {
    url.searchParams.append('after', queryParams.after)
  }
  if (queryParams.dropTracker) {
    url.searchParams.append('dropTracker', 'true')
  }

  try {
    const response = await fetch(url.toString())

    if (!response.ok) {
      throw response
    }

    const json: {
      locks: Lock[]
      last?: string
    } = await response.json()

    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function getScheduledEvents(documentId: string) {
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/schedule`,
    window.location.origin
  )

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
    })

    if (!response.ok) {
      throw response
    }

    const json: ScheduledEvents = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function schedulePublish(
  documentId: string,
  scheduledPublishDate: string,
  displayDate: string
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/schedule/publish`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
    setLastActiveTime(documentId)
  }

  try {
    const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/

    if (!isoRegex.test(scheduledPublishDate) || !isoRegex.test(displayDate)) {
      throw new Error('Invalid dates: Dates should be provided in ISO format.')
    }

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        scheduledPublishDate,
        displayDate,
      }),
    })

    if (!response.ok) {
      throw response
    }

    const json: { message: string } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function unschedulePublish(documentId: string) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/schedule/publish`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw response
    }

    const json: { message: string } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function scheduleUnpublish(
  documentId: string,
  scheduledUnpublishDate: string
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/schedule/unpublish`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        scheduledUnpublishDate,
      }),
    })

    if (!response.ok) {
      throw response
    }

    const json: { message: string } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function unscheduleUnpublish(documentId: string) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )
  const url = new URL(
    `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/schedule/unpublish`,
    window.location.origin
  )
  if (isDraftApiStatefulEnabled) {
    url.searchParams.append('stateful', 'true')
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw response
    }

    const json: { message: string } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function initiateTakeover(documentId: string) {
  try {
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/takeover`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw response
    }

    const json: { takeoverInitiated: boolean } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function resolveTakeover({
  preventTakeover,
  documentId,
  initiatorUserId,
  initiatorDisplayName,
  story,
}: {
  preventTakeover: boolean
  documentId: string
  initiatorUserId: string
  initiatorDisplayName: string
  story?: Story
}) {
  try {
    delete story?.isDirty
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/takeover/resolve?stateful=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'If-Match': arcPubETag ?? '',
        },
        body: JSON.stringify({
          preventTakeover,
          initiatorUserId,
          initiatorDisplayName,
          story,
        }),
      }
    )

    if (!response.ok) {
      throw response
    }

    const json: { success: boolean } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function deleteStory(
  documentId: string,
  dispatch: DispatchProp['dispatch']
) {
  try {
    const url = new URL(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}`,
      window.location.origin
    )

    const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
      'draftApiStateful'
    )
    if (isDraftApiStatefulEnabled) {
      url.searchParams.append('stateful', 'true')
    }

    dispatch(setStoryOperationInProgress(true))

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw response
    }

    dispatch(setStoryOperationInProgress(false))

    const json: PubstackDocument = await response.json()
    return json
  } catch (error) {
    dispatch(setStoryOperationInProgress(false))
    throw await handleError(error)
  }
}

export async function requestLock(documentId: string) {
  try {
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/lock/request?stateful=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw response
    }

    const json: { lockRequested: boolean } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function cancelLockRequest(documentId: string) {
  try {
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/lock/request?stateful=true`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw response
    }

    const json: { lockRequestCanceled: boolean } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function resolveLockRequest(
  documentId: string,
  payload: {
    accepted: boolean
    story?: Story
    initiatorUserId: string
    initiatorDisplayName: string
  }
) {
  try {
    delete payload.story?.isDirty
    const response = await fetch(
      `${TRIDENT_ROUTE_COMPOSER_API}/v2/story/${documentId}/lock/request?stateful=true`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'If-Match': arcPubETag ?? '',
        },
        body: JSON.stringify(payload),
      }
    )

    if (!response.ok) {
      throw response
    }

    const json: { accepted: boolean } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}

export async function resolveUrl(
  websiteId: string,
  documentId: string,
  ans: PubstackRevision['ans']
) {
  const isDraftApiStatefulEnabled = (await getFeatures()).isEnabled(
    'draftApiStateful'
  )

  const isStagingOrg =
    window.localStorage['arc.organization'] === 'staging' ||
    window.localStorage['arc.organization'] === 'sandbox.staging'

  const url = new URL(
    `/${
      isDraftApiStatefulEnabled && isStagingOrg
        ? 'draft-stateful'
        : 'internal/draft'
    }/v1/url-format-rule/${websiteId}/resolve`,
    window.location.origin
  )

  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        _id: documentId,
        ...ans,
      }),
    })

    if (!response.ok) {
      throw new AppError({
        status: response.status,
        statusText: response.statusText,
        errorKey: 'resolveUrlError',
        errorData: await response.json(),
      })
    }

    const json: {
      website_url: string
      format: {
        id: string
        format: string
        priority: number
        criteria: unknown
        default: boolean
      }
    } = await response.json()
    return json
  } catch (error) {
    throw await handleError(error)
  }
}
